import '../../audiences/audience_export_snapshot/index.js';
import type AudienceExportActions from './index.js';
import type {
  ImmutableProgram,
  ImmutableResult,
  InitialProperties,
  UpdateHandler,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
  type Cmd,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {html} from 'lit';
import {repeat} from 'lit/directives/repeat.js';
import {nothing} from 'lit/html.js';
import {isUndefined} from '@gl/lit/utils/index.js';
import type {AudienceExport} from '@gl/lit/types/audience_exports.js';
import {IngestStatus} from '@gl/lit/types/audience_exports.js';
import {
  ActionButtonKeys,
  renderDisableButton,
  renderEnableButton,
  renderExtendButton,
  renderIngestButton,
  renderManageButton,
  renderRunButton,
  renderViewInsightsButton,
} from './actions.js';
import {renderAuthenticateButton} from './actions.js';
import {createOpenManageModalEvent, isDisabled, isVisible} from './utils.js';
import type {
  LoopTrackerState,
  LoopTrackingData,
} from '@gl/stores/audience/loop_tracker.js';
import {audienceStore} from '@gl/stores/audience/index.js';
import {AudienceShowTabs} from '@gl/lit/types/audiences.js';
import type {SwitchPanelEventDetail} from '../audience_show_panels/index.js';
import {SWITCH_PANEL_EVENT_KEY} from '../audience_show_panels/index.js';
import {classMap} from 'lit/directives/class-map.js';
import type {ActionButtonOption, IngestModalEventDetail} from './types.js';
import {
  createActionHandler,
  createRenderFn,
  GLOBAL_AE_EVENT_KEYS,
} from './types.js';
import {featureFlagsManager} from '@gl/lit/lib/feature_flags/FeatureFlagsManager.js';
import {AlertIntent} from '@gl/lit/components/base/alert/index.js';
import {audienceExportsService} from '@gl/lit/services/audience_exports.js';
import {renderNotification} from '@gl/lit/utils/notifications.js';
import {audienceExportLang} from '@gl/locale/translations/audiences/exports.js';
import {locale} from '@gl/locale';
import {convertObjectToCase, snakeToCamel} from '@gl/utils/transform.js';
import type {AudienceExport as AudienceExportType} from '@gl/types/audiences/exports.js';

const lang = audienceExportLang[locale.getLocale()];
const INGEST_MODAL_EVENT_KEY = 'ae:ingest-modal:ingest';

export type Model = {
  audienceExport: AudienceExport;
  frequencies: Record<string, string>;
  destinationName: string;
  destinationIcon: string;
  destinationPlatformType: string;
  destinationNeedsReauthentication: boolean;
  destinationPath: string;
  label: string;
  isExpired: boolean;
  manageEnabled: boolean;
  open: boolean;
  loopProgress: LoopTrackingData;
  highlightDropdown: boolean;
};

export const Msg = makeVariants({
  setOpen: Variant<boolean>()('set-open'),
  setExportEnabled: Variant<boolean>()('set-export-enabled'),
  handleAction: Variant<ActionButtonKeys>()('handle-action'),
  setLoopProgress: Variant<LoopTrackingData>()('set-loop-progress'),
  setHighlightDropdown: Variant<boolean>()('set-highlight-dropdown'),
});

export async function changeIsEnabled(
  model: Model,
  handleUpdate?: UpdateHandler<Model, Msg>
) {
  if (!handleUpdate) return;
  try {
    await audienceExportsService.switchEnabled(
      model.audienceExport.audience_id,
      model.audienceExport.id,
      !model.audienceExport.is_enabled
    );
    handleUpdate(Msg.setExportEnabled(!model.audienceExport.is_enabled));
    renderNotification(
      `export ${model.audienceExport.is_enabled ? 'enabled' : 'disabled'}!`,
      AlertIntent.SUCCESS
    );
    location.reload(); // TODO: remove this when we have a better way to update the table
  } catch (e) {
    console.error(e);
    renderNotification('export could not be updated!', AlertIntent.DANGER);
  } finally {
    handleUpdate(Msg.setOpen(false));
  }
}

export const AudienceExportActionButtons: Record<
  ActionButtonKeys,
  ActionButtonOption
> = {
  [ActionButtonKeys.AUTHENTICATE]: {
    key: ActionButtonKeys.AUTHENTICATE,
    label: lang.actions.authenticate,
    order: 0,
    getVisibility: (model: Model) => ({
      visible: model.destinationNeedsReauthentication,
      disabled: false,
    }),
    renderContent: createRenderFn(({model, option, handleUpdate}) =>
      renderAuthenticateButton(model, option, handleUpdate)
    ),
    handleAction: createActionHandler(({model, option, handleUpdate}) => {
      if (isDisabled(model, option)) return;
      handleUpdate(Msg.setOpen(false));
      if (model.destinationPath) {
        location.href = model.destinationPath;
      }
    }),
  },
  /* This component supports enabling/disabling ongoing campaigns, but we don't
   * want to do it from the component for now
   * (we will keep the radio button in the exports table)
   * We can remove the radio button and make these actions visible
   * when we are ready to support it
   */
  [ActionButtonKeys.ENABLE]: {
    key: ActionButtonKeys.ENABLE,
    label: lang.actions.enable,
    order: 1,
    getVisibility: () => ({
      visible: false, // Make visible when we support enabling exports for ongoing campaign types
      disabled: false,
    }),
    renderContent: createRenderFn(({model, option, handleUpdate}) =>
      renderEnableButton(model, option, handleUpdate)
    ),
    handleAction: createActionHandler(({model, option, handleUpdate}) => {
      if (isDisabled(model, option)) return;
      changeIsEnabled(model, handleUpdate);
    }),
  },
  [ActionButtonKeys.DISABLE]: {
    key: ActionButtonKeys.DISABLE,
    label: lang.actions.disable,
    order: 1,
    getVisibility: () => ({
      visible: false, // Make visible when we support disabling exports for ongoing campaign types
      disabled: false,
    }),
    renderContent: createRenderFn(({model, option, handleUpdate}) =>
      renderDisableButton(model, option, handleUpdate)
    ),
    handleAction: createActionHandler(({model, option, handleUpdate}) => {
      if (isDisabled(model, option)) return;
      changeIsEnabled(model, handleUpdate);
    }),
  },
  [ActionButtonKeys.INGEST]: {
    key: ActionButtonKeys.INGEST,
    label: lang.actions.ingest,
    order: 1,
    getVisibility: (model: Model) => ({
      visible:
        featureFlagsManager.enabled('loopTracker') &&
        model.loopProgress.status >= 33 &&
        model.loopProgress.status < 66,
      disabled:
        model.audienceExport?.ingest_status === IngestStatus.NOT_SUPPORTED,
    }),
    renderContent: createRenderFn(({model, option, handleUpdate}) =>
      renderIngestButton(model, option, handleUpdate)
    ),
    handleAction: createActionHandler(({model, option, handleUpdate}) => {
      if (isDisabled(model, option)) return;
      handleUpdate(Msg.setHighlightDropdown(false));
      const audienceExport = convertObjectToCase<AudienceExportType>(
        model.audienceExport,
        snakeToCamel
      );
      window.dispatchEvent(
        new CustomEvent<IngestModalEventDetail>(INGEST_MODAL_EVENT_KEY, {
          detail: {
            audienceExport: {
              ...audienceExport,
              platformType: model.destinationPlatformType,
            },
          },
        })
      );
    }),
  },
  [ActionButtonKeys.VIEW_INSIGHTS]: {
    key: ActionButtonKeys.VIEW_INSIGHTS,
    label: lang.actions.viewInsights,
    order: 1,
    getVisibility: (model: Model) => ({
      visible:
        featureFlagsManager.enabled('loopTracker') &&
        model.loopProgress.status >= 33,
      disabled: model.loopProgress.status < 66,
    }),
    renderContent: createRenderFn(({model, option}) =>
      renderViewInsightsButton(model, option)
    ),
    handleAction: createActionHandler(({model, option}) => {
      if (isDisabled(model, option)) return;
      window.dispatchEvent(
        new CustomEvent<SwitchPanelEventDetail>(SWITCH_PANEL_EVENT_KEY, {
          detail: {panel: AudienceShowTabs.INSIGHTS},
        })
      );
    }),
  },
  [ActionButtonKeys.RUN]: {
    key: ActionButtonKeys.RUN,
    label: lang.actions.run,
    order: 2,
    getVisibility: (model: Model) => ({
      visible: !model.destinationNeedsReauthentication,
      disabled: false,
    }),
    renderContent: createRenderFn(({model}) => renderRunButton(model)),
  },
  [ActionButtonKeys.MANAGE]: {
    key: ActionButtonKeys.MANAGE,
    label: lang.actions.manage,
    order: 3,
    getVisibility: (model: Model) => ({
      visible:
        model.manageEnabled &&
        !model.isExpired &&
        !model.destinationNeedsReauthentication,
      disabled: false,
    }),
    renderContent: createRenderFn(({model, option}) =>
      renderManageButton(model, option)
    ),
    handleAction: createActionHandler(({model, option}) => {
      if (isDisabled(model, option)) return;
      const event = createOpenManageModalEvent(model, option);
      dispatchEvent(event);
    }),
  },
  [ActionButtonKeys.EXTEND]: {
    key: ActionButtonKeys.EXTEND,
    label: lang.actions.extend,
    order: 3,
    getVisibility: (model: Model) => ({
      visible:
        model.manageEnabled &&
        model.isExpired &&
        !model.destinationNeedsReauthentication,
      disabled: false,
    }),
    renderContent: createRenderFn(({model, option}) =>
      renderExtendButton(model, option)
    ),
    handleAction: createActionHandler(({model, option}) => {
      if (isDisabled(model, option)) return;
      const event = createOpenManageModalEvent(model, option);
      dispatchEvent(event);
    }),
  },
} as const;

export type Msg = VariantsFor<typeof Msg>;

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const audienceExport = <AudienceExport>props.get('audienceExport');
  const label = <string>props.get('label') ?? '';
  const frequencies = <Record<string, string>>props.get('frequencies') ?? {};
  const destinationName = <string>props.get('destinationName') ?? '';
  const destinationIcon = <string>props.get('destinationIcon') ?? '';
  const destinationPlatformType =
    <string>props.get('destinationPlatformType') ?? '';
  const destinationPath = <string>props.get('destinationPath') ?? '';
  const isExpired = <boolean>props.get('isExpired');
  const manageEnabled = <boolean>props.get('manageEnabled');
  const destinationNeedsReauthentication = <boolean>(
    props.get('destinationNeedsReauthentication')
  );
  const loopProgress = <LoopTrackingData>props.get('loopProgress') ?? {
    status: 0,
    goal: 100,
  };

  return [
    {
      audienceExport,
      frequencies,
      destinationName,
      destinationIcon,
      destinationPlatformType,
      destinationPath,
      label,
      isExpired,
      manageEnabled,
      destinationNeedsReauthentication,
      open: false,
      loopProgress,
      highlightDropdown: false,
    },
    cmd((host: AudienceExportActions) => {
      attachListeners(host);
      if (audienceExport.audience_id) {
        const initialState = audienceStore.loopTracker.getState();
        const initialProgress = initialState.getProgressData(
          audienceExport.audience_id
        );
        if (initialProgress) {
          host.handleUpdate(Msg.setLoopProgress(initialProgress));
        }

        const updateLoopProgress = (state: LoopTrackerState) => {
          const progressData = state.getProgressData(
            audienceExport.audience_id
          );
          if (progressData) {
            host.handleUpdate(Msg.setLoopProgress(progressData));
          }
        };

        const unsubscribe =
          audienceStore.loopTracker.subscribe(updateLoopProgress);

        return () => unsubscribe();
      }
    }),
  ] as const;
}

function attachListeners(host: AudienceExportActions) {
  const listeners = [
    {
      event: GLOBAL_AE_EVENT_KEYS.INGEST,
      callback: () => {
        host.handleUpdate(Msg.setHighlightDropdown(true));
      },
    },
    {
      event: GLOBAL_AE_EVENT_KEYS.VIEW_INSIGHTS,
      callback: () => {
        host.handleUpdate(Msg.handleAction(ActionButtonKeys.VIEW_INSIGHTS));
      },
    },
  ];
  listeners.forEach(({event, callback}) => {
    window.addEventListener(event, callback);
  });

  return () => {
    listeners.forEach(({event, callback}) => {
      window.removeEventListener(event, callback);
    });
  };
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-open':
      return {...model, open: msg.value} as const;
    case 'set-export-enabled':
      return {
        ...model,
        audienceExport: {...model.audienceExport, is_enabled: msg.value},
      } as const;
    case 'handle-action':
      return [
        model,
        cmd((host: AudienceExportActions) => {
          const option = AudienceExportActionButtons[msg.value];
          if (option) option.handleAction?.(model, option, host.handleUpdate);
        }),
      ] as const;
    case 'set-loop-progress':
      return {...model, loopProgress: msg.value} as const;
    case 'set-highlight-dropdown':
      return [
        {...model, highlightDropdown: msg.value},
        cmd((host: AudienceExportActions) => {
          if (msg.value) {
            const timeout = setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent<SwitchPanelEventDetail>(
                  SWITCH_PANEL_EVENT_KEY,
                  {
                    detail: {panel: AudienceShowTabs.EXPORTS},
                  }
                )
              );
              host.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            }, 100);

            return () => clearTimeout(timeout);
          }
        }),
      ] as const;
  }
}

function renderStatusOptions(
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  if (!model.open) return nothing;

  const options = Object.values(AudienceExportActionButtons).filter(option =>
    isVisible(model, option)
  );

  return html`<div slot="surface">
    <fly-list>
      ${repeat(
        options,
        o => o.key,
        o => o.renderContent(model, o, handleUpdate)
      )}
    </fly-list>
  </div>`;
}

export const hasEnabledActions = (model: Model) => {
  return Object.values(AudienceExportActionButtons).some(
    option => option.getVisibility?.(model).visible
  );
};

function view(model: Readonly<Model>, handleUpdate: UpdateHandler<Model, Msg>) {
  if (!hasEnabledActions(model)) return nothing;
  if ([model.audienceExport].some(isUndefined)) {
    return nothing;
  }

  return html`
    <div class="audience-actions-container">
      <fly-dropdown-v2
        class=${classMap({
          'audience-actions-dropdown': true,
          'highlight-dropdown':
            model.highlightDropdown &&
            model.audienceExport.ingest_status === IngestStatus.NOT_INGESTED,
        })}
        ?open=${model.open}
        optionsPlacement="bottom-start"
        updateOnAnimationFrame
        closeWhenClickOutside
        @open=${() => handleUpdate(Msg.setOpen(true))}
        @close=${() => handleUpdate(Msg.setOpen(false))}
      >
        <div slot="anchor" class="audience-action-button">
          <button class="audience-action-button__anchor">
            <fly-icon icon="more_vert"></fly-icon>
          </button>
        </div>
        <div slot="content">${renderStatusOptions(model, handleUpdate)}</div>
      </fly-dropdown-v2>
    </div>
  `;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
